<template>
  <div id="content" style="height: 100vh">
    <NavbarNotLoggedIn />

    <div class="grid place-items-center h-[80vh]">
      <div class="w-[420px] rounded overflow-hidden shadow-lg">
        <div class="px-6 py-6">
          <div class="text-left mb-4">
            <router-link to="/" class="text-sm text-gray-500 hover:text-black">
              ← Back to Login
            </router-link>
          </div>
          <div class="font-bold text-xl mb-1" style="border-bottom: 1px solid black; padding-bottom: 8px">
            Forgot Password
          </div>

          <!-- Step 1: Enter email -->
          <div v-if="step === 1">
            <p class="text-sm text-gray-500 mt-4 mb-6">
              Enter your email address and we'll send you an OTP to reset your password.
            </p>
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                v-model="email"
                type="email"
                placeholder="your@email.com"
                class="w-full border-2 border-gray-300 rounded py-2 px-4 text-sm focus:outline-none focus:border-orange-600"
              />
            </div>
            <p v-if="errorMsg" class="text-red-500 text-sm mb-3">{{ errorMsg }}</p>
            <button
              @click="sendOtp"
              :disabled="loading"
              class="w-full bg-black text-white font-bold py-2 rounded hover:bg-gray-800 disabled:opacity-60"
            >
              {{ loading ? 'Sending OTP...' : 'Send OTP' }}
            </button>
          </div>

          <!-- Step 2: Enter OTP + new password -->
          <div v-if="step === 2">
            <p class="text-sm text-gray-500 mt-4 mb-6">
              Enter the OTP sent to <strong>{{ email }}</strong> and set your new password.
            </p>
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">OTP</label>
              <input
                v-model="otp"
                type="text"
                placeholder="Enter OTP"
                class="w-full border-2 border-gray-300 rounded py-2 px-4 text-sm focus:outline-none focus:border-orange-600"
              />
            </div>
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <div class="relative">
                <input
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="Min 8 characters"
                  class="w-full border-2 border-gray-300 rounded py-2 px-4 pr-10 text-sm focus:outline-none focus:border-orange-600"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-black"
                  aria-label="Toggle new password visibility"
                >
                  <i :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
                </button>
              </div>
            </div>
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <div class="relative">
                <input
                  v-model="confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  placeholder="Re-enter password"
                  class="w-full border-2 border-gray-300 rounded py-2 px-4 pr-10 text-sm focus:outline-none focus:border-orange-600"
                />
                <button
                  type="button"
                  @click="showConfirmPassword = !showConfirmPassword"
                  class="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-black"
                  aria-label="Toggle confirm password visibility"
                >
                  <i :class="showConfirmPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
                </button>
              </div>
            </div>
            <p v-if="errorMsg" class="text-red-500 text-sm mb-3">{{ errorMsg }}</p>
            <button
              @click="resetPassword"
              :disabled="loading"
              class="w-full bg-black text-white font-bold py-2 rounded hover:bg-gray-800 disabled:opacity-60 mb-3"
            >
              {{ loading ? 'Resetting...' : 'Reset Password' }}
            </button>
            <button @click="step = 1; errorMsg = ''" class="w-full text-sm text-gray-500 hover:text-black">
              ← Change email
            </button>
          </div>

          <!-- Step 3: Success -->
          <div v-if="step === 3" class="text-center py-6">
            <div class="text-green-600 text-5xl mb-4">✓</div>
            <p class="font-semibold text-lg mb-2">Password Reset Successful</p>
            <p class="text-sm text-gray-500 mb-6">You can now log in with your new password.</p>
            <router-link
              to="/"
              class="bg-black text-white font-bold py-2 px-6 rounded hover:bg-gray-800"
            >Go to Login</router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../boot/axios";
import NavbarNotLoggedIn from "@/components/NavbarNotLoggedIn.vue";

export default {
  name: "ForgotPassword",
  components: { NavbarNotLoggedIn },
  data() {
    return {
      step: 1,
      email: "",
      otp: "",
      password: "",
      confirmPassword: "",
      showPassword: false,
      showConfirmPassword: false,
      loading: false,
      errorMsg: "",
    };
  },
  methods: {
    async sendOtp() {
      if (!this.email) { this.errorMsg = "Email is required."; return; }
      this.loading = true;
      this.errorMsg = "";
      try {
        const res = await api.post("/auth/forgot_password", { email: this.email });
        if (res.data.status === true) {
          this.step = 2;
        } else {
          this.errorMsg = res.data.message || "Failed to send OTP.";
        }
      } catch (err) {
        this.errorMsg = err?.response?.data?.message || "Something went wrong.";
      } finally {
        this.loading = false;
      }
    },

    async resetPassword() {
      if (!this.otp) { this.errorMsg = "OTP is required."; return; }
      if (!this.password) { this.errorMsg = "New password is required."; return; }
      if (this.password !== this.confirmPassword) { this.errorMsg = "Passwords do not match."; return; }
      if (this.password.length < 8) { this.errorMsg = "Password must be at least 8 characters."; return; }

      this.loading = true;
      this.errorMsg = "";
      try {
        const res = await api.post("/auth/validate_otp", {
          email: this.email,
          otp: this.otp,
          password: this.password,
          confirm_password: this.confirmPassword,
        });
        if (res.data.status === true) {
          this.step = 3;
        } else {
          this.errorMsg = res.data.message || "Reset failed.";
        }
      } catch (err) {
        this.errorMsg = err?.response?.data?.message || "Something went wrong.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>